import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFeaturePreviewStore } from '@/state/featurePreviewStore.js';
import { useAuthStore } from '@/state/authStore.js';
import { useUiStore } from '@/state/uiStore.js';
import {
  applySidebarOrder,
  groupSidebarTabs,
  moveSidebarTab,
  readClosedGroups,
  readSidebarOrder,
  writeClosedGroups,
  writeSidebarOrder
} from './sidebarOrder.js';

/**
 * Sidebar navigation, grouped (2026-10-27): Home on its own, then the tabs
 * under six headings — People, Devices & apps, Infrastructure, Files & sites,
 * Security & compliance, Insight & automation. Each heading opens and closes
 * (remembered per person); a closed group shows how many tabs it holds.
 * Collapsed to the icon rail, thin lines separate the groups and hovering an
 * icon pops out its group with the other tabs in it.
 *
 * Kept from the monolith: the button `id`s and `data-tab` attributes, the
 * Font Awesome icons and the labels. A tab the person has no permission for
 * is left out, and a group with nothing left in it disappears.
 *
 * Drag to reorder (or Alt+Up/Down) still works, within a group.
 */

/** Group headings, top to bottom. */
export const SIDEBAR_GROUPS = [
  { id: 'people', label: 'People' },
  { id: 'devices', label: 'Devices & apps' },
  { id: 'infrastructure', label: 'Infrastructure' },
  { id: 'files', label: 'Files & sites' },
  { id: 'security', label: 'Security & compliance' },
  { id: 'insight', label: 'Insight & automation' }
];

/**
 * @typedef {Object} SidebarTab
 * @property {string} id           Sidebar button DOM id (monolith parity)
 * @property {string} tabId        Tab id passed to switchTab / activeTab
 * @property {string} icon         Font Awesome icon class
 * @property {string} label        Button text
 * @property {string | null} permission   Permission id, or null = always visible
 * @property {string | null} group SIDEBAR_GROUPS id, or null = above the groups
 */

/** @type {SidebarTab[]} */
export const SIDEBAR_TABS = [
  { id: 'sidebarDashboardTab',        tabId: 'dashboard',           icon: 'fa-home',         label: 'Home',               permission: null, group: null },

  { id: 'sidebarUserManagementTab',   tabId: 'user-management-v2',  icon: 'fa-user',         label: 'User Management',    permission: 'user-management', group: 'people' },
  { id: 'sidebarGroupManagementTab',  tabId: 'group-management',    icon: 'fa-users',        label: 'Group Management',   permission: 'group-management', group: 'people' },
  { id: 'sidebarSharedMailboxesTab',  tabId: 'shared-mailboxes',    icon: 'fa-envelope',     label: 'Shared Mailboxes',   permission: 'shared-mailboxes', group: 'people' },

  { id: 'sidebarAssetManagementTab',  tabId: 'asset-management',    icon: 'fa-laptop',       label: 'Asset Management',   permission: 'asset-management', group: 'devices' },
  // Apps tab: the app-management permission (out of preview 2026-10-26).
  { id: 'sidebarAppsTab',             tabId: 'apps',                icon: 'fa-th-large',     label: 'Apps',               permission: 'app-management', group: 'devices' },
  { id: 'sidebarPolicyFinderTab',     tabId: 'policy-finder',       icon: 'fa-search',       label: 'Policy Finder',      permission: 'policy-finder', group: 'devices' },
  { id: 'sidebarPrinterManagementTab',tabId: 'printer-management',  icon: 'fa-print',        label: 'Printer Management', permission: 'printer-management', group: 'devices' },

  { id: 'sidebarSharesTab',           tabId: 'shares',              icon: 'fa-server',       label: 'Server Management',  permission: 'server-management', group: 'infrastructure' },
  { id: 'sidebarToolsTab',            tabId: 'tools',               icon: 'fa-layer-group',  label: 'Host Pools',         permission: 'tools', group: 'infrastructure' },
  { id: 'sidebarNetworkTab',          tabId: 'network-dashboard',   icon: 'fa-network-wired',label: 'Network',            permission: 'network-dashboard', group: 'infrastructure' },
  // Read-only Azure Backup visibility (2026-10-16); server gate requirePermission('backups').
  { id: 'sidebarBackupsTab',          tabId: 'backups',             icon: 'fa-database',     label: 'Backups',            permission: 'backups', group: 'infrastructure' },

  { id: 'sidebarAdminTab',            tabId: 'admin',               icon: 'fa-hdd',          label: 'Drive Management',   permission: 'drive-management', group: 'files' },
  { id: 'sidebarSharePointTab',       tabId: 'sharepoint-management',icon: 'fa-cloud',       label: 'SharePoint',         permission: 'sharepoint-management', group: 'files' },

  { id: 'sidebarSecurityTab',         tabId: 'security-dashboard',  icon: 'fa-shield-alt',   label: 'Security Hub',       permission: 'security-dashboard', group: 'security' },
  { id: 'sidebarComplianceTab',       tabId: 'compliance',          icon: 'fa-clipboard-check', label: 'Compliance',      permission: 'compliance', group: 'security' },

  // Advanced Reporting (Log Analytics) is a sub-tab inside Reporting,
  // permission-gated by 'log-analytics'.
  { id: 'sidebarReportingTab',        tabId: 'reporting',           icon: 'fa-chart-line',   label: 'Reporting',          permission: 'reporting', group: 'insight' },
  // Grantable like every other feature tab (2026-07-27 — was adminOnly). It
  // exposes financial data, so grant with care; server-side gate is
  // requirePermission('cost-analysis') on every /api/costs route.
  { id: 'sidebarCostAnalysisTab',     tabId: 'cost-analysis',       icon: 'fa-sterling-sign',label: 'Cost Analysis',      permission: 'cost-analysis', group: 'insight' },
  { id: 'sidebarAutomationsTab',      tabId: 'automations',         icon: 'fa-robot',        label: 'Automations',        permission: 'automations', group: 'insight' },
  // Claude Code usage per person (2026-10-29); server gate requirePermission('claude-usage').
  { id: 'sidebarClaudeUsageTab',      tabId: 'claude-usage',        icon: 'fa-chart-column', label: 'Claude Usage',       permission: 'claude-usage', group: 'insight' }
  // Connector Admin is no longer a sidebar tab — it now lives in
  // components/connector/ConnectorModal.jsx, opened from the Dashboard
  // Connector widget and Admin Settings → Infrastructure.
];

/** Hover pop-out for the collapsed rail: the group's name and its tabs. */
function GroupFlyout({ flyout, activeTab, onPick, onEnter, onLeave }) {
  if (!flyout) return null;
  return createPortal(
    <div
      className="sidebar-flyout"
      role="menu"
      aria-label={flyout.label}
      style={{ top: flyout.top, left: flyout.left }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {flyout.label && <div className="sidebar-flyout-title">{flyout.label}</div>}
      {flyout.tabs.map((tab) => (
        <button
          key={tab.tabId}
          type="button"
          role="menuitem"
          className={`sidebar-flyout-item${activeTab === tab.tabId ? ' active' : ''}`}
          onClick={() => onPick(tab.tabId)}
        >
          <i className={`fas fa-fw ${tab.icon}`} aria-hidden="true" />
          <span>{tab.label}</span>
        </button>
      ))}
    </div>,
    document.body
  );
}

export default function Sidebar() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const activeTab = useUiStore((s) => s.activeTab);
  const setActiveTab = useUiStore((s) => s.setActiveTab);
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebarCollapsed = useUiStore((s) => s.toggleSidebarCollapsed);
  const sidebarMobileOpen = useUiStore((s) => s.sidebarMobileOpen);
  const setSidebarMobileOpen = useUiStore((s) => s.setSidebarMobileOpen);
  const previews = useFeaturePreviewStore((s) => s.enabled);
  const userId = useAuthStore((s) => s.user?.id);

  // Per-person menu order (drag to reorder, or Alt+Up/Down on a focused item).
  const [order, setOrder] = useState(() => readSidebarOrder(userId));
  // Per-person closed group headings.
  const [closedGroups, setClosedGroups] = useState(() => readClosedGroups(userId));
  useEffect(() => {
    setOrder(readSidebarOrder(userId));
    setClosedGroups(readClosedGroups(userId));
  }, [userId]);
  const orderedTabs = useMemo(() => applySidebarOrder(SIDEBAR_TABS, order), [order]);
  const [dragId, setDragId] = useState(null);
  const [dropHint, setDropHint] = useState(null); // { tabId, position }
  const [flyout, setFlyout] = useState(null); // { label, tabs, top, left }
  const flyoutTimer = useRef(null);
  const navRef = useRef(null);

  // The pop-out only belongs to the collapsed rail.
  useEffect(() => { if (!sidebarCollapsed) setFlyout(null); }, [sidebarCollapsed]);
  useEffect(() => () => clearTimeout(flyoutTimer.current), []);

  const saveOrder = (next) => {
    setOrder(next);
    writeSidebarOrder(userId, next);
  };
  const moveTab = (movingId, targetId, position) => {
    const ids = orderedTabs.map((t) => t.tabId);
    const next = moveSidebarTab(ids, movingId, targetId, position);
    if (next !== ids) saveOrder(next);
  };
  const resetOrder = () => {
    setOrder(null);
    writeSidebarOrder(userId, null);
  };
  const toggleGroup = (groupId) => {
    const next = closedGroups.includes(groupId)
      ? closedGroups.filter((g) => g !== groupId)
      : [...closedGroups, groupId];
    setClosedGroups(next);
    writeClosedGroups(userId, next);
  };

  const visibleTabs = orderedTabs.filter((tab) => {
    if (tab.adminOnly && !isAdmin) return false;
    if (tab.permission && !hasPermission(tab.permission)) return false;
    if (tab.preview && !previews.includes(tab.preview)) return false;
    return true;
  });
  const sections = groupSidebarTabs(visibleTabs, SIDEBAR_GROUPS);
  const groupOf = (tabId) => SIDEBAR_TABS.find((t) => t.tabId === tabId)?.group ?? null;

  const pickTab = (tabId) => {
    setActiveTab(tabId);
    setFlyout(null);
    // On mobile the sidebar is a slide-in drawer over the content — picking a
    // tab closes it (desktop's pinned sidebar never sets sidebarMobileOpen).
    setSidebarMobileOpen(false);
  };

  const showFlyout = (event, section) => {
    if (!sidebarCollapsed) return;
    clearTimeout(flyoutTimer.current);
    const rect = event.currentTarget.getBoundingClientRect();
    setFlyout({
      label: section.label,
      tabs: section.tabs,
      top: Math.max(64, Math.min(rect.top - 8, window.innerHeight - 60 - section.tabs.length * 38)),
      left: rect.right + 8
    });
  };
  const hideFlyoutSoon = () => {
    clearTimeout(flyoutTimer.current);
    flyoutTimer.current = setTimeout(() => setFlyout(null), 180);
  };

  // Alt+Up/Down moves a tab within its own group (visible order).
  const handleKeyMove = (event, tab, sectionTabs) => {
    if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
    event.preventDefault();
    const at = sectionTabs.findIndex((t) => t.tabId === tab.tabId);
    const neighbour = sectionTabs[event.key === 'ArrowUp' ? at - 1 : at + 1];
    if (!neighbour) return;
    moveTab(tab.tabId, neighbour.tabId, event.key === 'ArrowUp' ? 'before' : 'after');
    // Keep focus on the moved item after React re-orders the buttons.
    requestAnimationFrame(() => navRef.current?.querySelector(`[data-tab="${tab.tabId}"]`)?.focus());
  };

  const renderItem = (tab, section) => {
    const isActive = activeTab === tab.tabId;
    const hint = dropHint?.tabId === tab.tabId ? dropHint.position : null;
    return (
      <button
        key={tab.id}
        type="button"
        id={tab.id}
        data-tab={tab.tabId}
        className={
          `sidebar-item${isActive ? ' active' : ''}` +
          (dragId === tab.tabId ? ' sidebar-item--dragging' : '') +
          (hint ? ` sidebar-item--drop-${hint}` : '')
        }
        draggable
        onDragStart={(event) => {
          setDragId(tab.tabId);
          setFlyout(null);
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData('text/plain', tab.tabId);
        }}
        onDragOver={(event) => {
          // Reordering stays inside a group.
          if (!dragId || dragId === tab.tabId || groupOf(dragId) !== tab.group) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          const rect = event.currentTarget.getBoundingClientRect();
          const position = event.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
          if (hint !== position) setDropHint({ tabId: tab.tabId, position });
        }}
        onDragLeave={() => {
          if (dropHint?.tabId === tab.tabId) setDropHint(null);
        }}
        onDrop={(event) => {
          event.preventDefault();
          if (dragId && hint) moveTab(dragId, tab.tabId, hint);
          setDragId(null);
          setDropHint(null);
        }}
        onDragEnd={() => {
          setDragId(null);
          setDropHint(null);
        }}
        onKeyDown={(event) => handleKeyMove(event, tab, section.tabs)}
        onMouseEnter={(event) => showFlyout(event, section)}
        onMouseLeave={hideFlyoutSoon}
        onClick={() => pickTab(tab.tabId)}
        aria-current={isActive ? 'page' : undefined}
        aria-label={sidebarCollapsed ? tab.label : undefined}
        aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
        title={sidebarCollapsed ? undefined : 'Drag to reorder within its group (or Alt+Up/Down)'}
      >
        <span className="sidebar-icon">
          <i className={`fas fa-fw ${tab.icon}`} aria-hidden="true" />
        </span>
        <span className="sidebar-text">{tab.label}</span>
      </button>
    );
  };

  const className =
    'sidebar' +
    (sidebarCollapsed ? ' collapsed' : '') +
    (sidebarMobileOpen ? ' mobile-open' : '');

  return (
    <>
      <nav className={className} id="sidebar" aria-label="Primary" ref={navRef}>
        <div className="sidebar-nav">
          {sections.map((section, index) => {
            if (!section.id) {
              return <div key="top" className="sidebar-group">{section.tabs.map((tab) => renderItem(tab, section))}</div>;
            }
            const closed = closedGroups.includes(section.id) && !sidebarCollapsed;
            const holdsActive = section.tabs.some((t) => t.tabId === activeTab);
            const listId = `sidebar-group-${section.id}`;
            return (
              <div key={section.id} className="sidebar-group">
                {index > 0 && <div className="sidebar-separator" aria-hidden="true" />}
                <button
                  type="button"
                  className={`sidebar-group-heading${closed && holdsActive ? ' holds-active' : ''}`}
                  aria-expanded={!closed}
                  aria-controls={listId}
                  onClick={() => toggleGroup(section.id)}
                  title={closed ? `Show ${section.label}` : `Hide ${section.label}`}
                >
                  <span className="sidebar-group-label">{section.label}</span>
                  {closed && <span className="sidebar-group-count">{section.tabs.length}</span>}
                  <i className={`fas fa-chevron-down sidebar-group-chevron${closed ? ' is-closed' : ''}`} aria-hidden="true" />
                </button>
                {!closed && (
                  <div id={listId} role="group" aria-label={section.label}>
                    {section.tabs.map((tab) => renderItem(tab, section))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="sidebar-footer">
          {order && (
            <button
              type="button"
              className="sidebar-reset-order-btn"
              onClick={resetOrder}
              title="Put the menu back in its default order"
              aria-label="Reset menu order"
            >
              <i className="fas fa-undo" aria-hidden="true" />
              <span className="sidebar-text">Reset menu order</span>
            </button>
          )}
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={toggleSidebarCollapsed}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <i
              className={`fas ${sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`}
              aria-hidden="true"
            />
          </button>
        </div>
      </nav>

      <GroupFlyout
        flyout={sidebarCollapsed ? flyout : null}
        activeTab={activeTab}
        onPick={pickTab}
        onEnter={() => clearTimeout(flyoutTimer.current)}
        onLeave={hideFlyoutSoon}
      />

      <div
        className="sidebar-overlay"
        id="sidebarOverlay"
        onClick={() => setSidebarMobileOpen(false)}
        style={{ display: sidebarMobileOpen ? 'block' : 'none' }}
        aria-hidden="true"
      />
    </>
  );
}
